/*
 * Contract of Sale — structured data-entry form (ticket 12525266778).
 *
 * Parallels AuthorityFormPage but for the Property Contract of Sale. The agent
 * fills a schema-driven tabbed form; answers persist to the envelope's
 * form_state via the cos-form / cos-form-values endpoints.
 *
 * Data entry only for now: generating and signing the REIV/LIV Contract of Sale
 * + Section 32 is enabled once the blank precedent PDF is added server-side.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronLeft, Save, ScrollText, Upload, ArrowRight, Search, X, Plus } from 'lucide-react';
import esignApi from '../../../api/esign';
import { getContacts } from '../../../api/contact';
import { MELBOURNE_TZ } from '../../../utils/melbourneTime';

const INPUT =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500';

// Today in Melbourne as YYYY-MM-DD. en-CA formats as ISO, and .format() isn't the
// toLocale*String() the agency-time lint rule restricts.
const melbourneToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: MELBOURNE_TZ }).format(new Date());

// Resolve a schema date bound: the "today" sentinel -> Melbourne's current date,
// an explicit YYYY-MM-DD -> itself, anything else -> undefined (no bound).
const resolveDateBound = (bound) =>
  bound === 'today' ? melbourneToday() : (bound || undefined);

// Add days/weeks/months to a YYYY-MM-DD date. UTC math keeps it a pure calendar
// shift with no daylight-saving drift; toISOString isn't the toLocale*String the
// agency-time lint rule restricts.
const shiftISODate = (baseISO, amount, unit) => {
  const [y, m, d] = baseISO.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (unit === 'month') dt.setUTCMonth(dt.getUTCMonth() + amount);
  else if (unit === 'week') dt.setUTCDate(dt.getUTCDate() + amount * 7);
  else dt.setUTCDate(dt.getUTCDate() + amount);
  return dt.toISOString().slice(0, 10);
};

// Derive the settlement due date from a free-text period like "30 days", "6 weeks"
// or "3 months", counted from today / the offer date (DOMA 13213368559). Returns
// '' when there's no leading number, so a non-numeric note leaves the date alone.
const settlementDateFromPeriod = (periodText) => {
  const m = /(\d+)\s*(day|week|month)?/i.exec(String(periodText || ''));
  if (!m) return '';
  const amount = parseInt(m[1], 10);
  if (!Number.isFinite(amount)) return '';
  return shiftISODate(melbourneToday(), amount, (m[2] || 'day').toLowerCase());
};

// A question with show_if ({key, includes}) is shown only when the named
// checkboxes answer includes that value — e.g. finance fields under "Subject to
// finance" (DOMA 13213465006).
const isVisible = (q, values) => {
  const cond = q.show_if;
  if (!cond) return true;
  const answer = values[cond.key];
  const picked = Array.isArray(answer) ? answer : [];
  return cond.includes ? picked.includes(cond.includes) : true;
};

function Field({ q, value, onChange, invalid }) {
  // A multi-choice answer is a list of picked values, the same shape the Buyer
  // Portal's own "Offer Details" question stores.
  if (q.type === 'checkboxes') {
    const picked = Array.isArray(value) ? value : [];
    const toggle = (option) =>
      onChange(q.key, picked.includes(option)
        ? picked.filter((p) => p !== option)
        : [...picked, option]);
    return (
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-1">
          {q.label}
          {q.required && <span className="text-red-500"> *</span>}
        </legend>
        {q.help && <p className="text-xs text-gray-500 mb-2">{q.help}</p>}
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-lg border p-3 ${
          invalid ? 'border-red-400' : 'border-gray-300'}`}>
          {(q.options || []).map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={picked.includes(o.value)}
                onChange={() => toggle(o.value)} />
              {o.label}
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  const common = {
    id: q.key,
    value: value ?? '',
    onChange: (e) => onChange(q.key, e.target.value),
    className: `${INPUT} ${invalid ? 'border-red-400 ring-1 ring-red-300' : ''}`,
  };
  const inputType =
    q.type === 'date' ? 'date' : q.type === 'email' ? 'email' : q.type === 'tel' ? 'tel' : 'text';
  return (
    <div>
      <label htmlFor={q.key} className="block text-sm font-medium text-gray-700 mb-1">
        {q.label}
        {q.required && <span className="text-red-500"> *</span>}
      </label>
      {q.type === 'textarea' ? (
        <textarea rows={3} placeholder={q.help || ''} {...common} />
      ) : q.type === 'currency' ? (
        // Show thousands-separator commas as the agent types ($1,000,000, not
        // 1000000 - ticket 12525266778) while STORING the raw number, so the
        // deposit/balance maths and the PDF bake never see a comma.
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
          <input
            id={q.key}
            type="text"
            inputMode="decimal"
            placeholder={q.help || ''}
            value={commaGroup(value)}
            onChange={(e) => onChange(q.key, e.target.value.replace(/,/g, ''))}
            className={`${INPUT} pl-6 ${invalid ? 'border-red-400 ring-1 ring-red-300' : ''}`}
          />
        </div>
      ) : (
        <input
          type={inputType}
          inputMode={q.type === 'number' ? 'decimal' : undefined}
          placeholder={q.help || ''}
          {...(q.type === 'date'
            ? { max: resolveDateBound(q.max), min: resolveDateBound(q.min) }
            : {})}
          {...common}
        />
      )}
    </div>
  );
}

// Group a raw numeric string with thousands-separator commas for display, while
// leaving the stored value comma-free. Shared by every currency field on the
// buyer sheet (offer price, deposit, balance, loan amount).
function commaGroup(v) {
  const raw = String(v ?? '').replace(/,/g, '');
  const m = /^(\d+)(\.\d*)?$/.exec(raw);
  // eslint-disable-next-line no-restricted-syntax -- number grouping, not a date
  return m ? Number(m[1]).toLocaleString('en-US') + (m[2] || '') : raw;
}

// Money as the agent types it ("$880,000") -> a number, and back again in the
// same shape the backend writes onto the contract ("880,000" / "880,000.50").
function money(text) {
  const digits = String(text ?? '').replace(/[^\d.-]/g, '');
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) ? n : null;
}

// Auto-calc result as a comma-free number string (rounded to cents), the same
// raw shape the currency inputs store; commaGroup adds the commas for display.
function rawMoney(n) {
  return String(Math.round(n * 100) / 100);
}

// Autocomplete for the buyer's solicitor/conveyancer: searches the shared saved
// directory (firms other agents entered before) and CRM contacts, and one-tap
// prefills the Legal practitioner fields (ticket 12525266778: "save the details
// for the next agents use"). Mirrors AuthorityFormPage's picker, but fills the
// buyer sheet's legal_rep_mobile (the authority uses legal_rep_phone).
function LegalRepPicker({ onPick }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const timer = useRef();

  const run = (text) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!text || text.trim().length < 2) { setMatches([]); return; }
      try {
        const [saved, contacts] = await Promise.all([
          esignApi.conveyancerSuggestions(text).then((r) => r.data).catch(() => []),
          getContacts({ search: text, page_size: 8 })
            .then((r) => (Array.isArray(r.data) ? r.data : (r.data.results || []))).catch(() => []),
        ]);
        // One entry per conveyancer, keyed on email identity (else name), keeping
        // the record that carries a firm so the saved directory's firm wins over a
        // firm-less CRM contact instead of both showing (DOMA 13145698774).
        const byKey = new Map();
        [...(saved || []), ...(contacts || [])].forEach((m) => {
          if (!m.name) return;
          const key = (m.email || '').trim().toLowerCase() || (m.name || '').trim().toLowerCase();
          if (!key) return;
          const firm = (m.firm || m.company || '').trim();
          const existing = byKey.get(key);
          if (!existing || (firm && !(existing.firm || existing.company || '').trim())) {
            byKey.set(key, m);
          }
        });
        setMatches([...byKey.values()].slice(0, 10));
      } catch { setMatches([]); }
    }, 250);
  };

  return (
    <div className="relative mb-4 sm:col-span-2">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Find solicitor / conveyancer <span className="text-gray-400 font-normal">(fills the details below)</span>
      </label>
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} placeholder="Search saved firms or contacts by name"
          onChange={(e) => { setQ(e.target.value); setOpen(true); run(e.target.value); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm" />
        {q && <button type="button" onClick={() => { setQ(''); setMatches([]); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><X className="w-4 h-4" /></button>}
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-40 left-0 right-0 top-full mt-1 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {matches.map((m, i) => {
            // Show the conveyancer's firm/company name first; fall back to the
            // contact/practitioner name or email when no firm is on record.
            const firm = m.firm || m.company || '';
            const primary = firm || m.name || '—';
            const secondary = firm ? (m.name || m.email || '') : (m.email || '');
            return (
              <button type="button" key={m.id ?? i} onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(m); setOpen(false); setQ(m.name || ''); }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm truncate">
                {primary}{secondary ? ` · ${secondary}` : ''}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ContractOfSaleFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [missing, setMissing] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  // How many purchaser cards are shown (1-4). Two always show; 3-4 appear once the
  // agent clicks "Add purchaser" or when saved data already fills those slots.
  const [visiblePurchasers, setVisiblePurchasers] = useState(2);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasContract, setHasContract] = useState(false);
  const [uploading, setUploading] = useState(false);
  // A deposit that arrived with the sheet came from the buyer's own offer, so it
  // is theirs, not ours to recalculate.
  const depositSet = useRef(false);
  // True once the agent has set a settlement date by hand, so the period-driven
  // auto-fill below stops overriding it (mirrors depositSet).
  const settlementDateSet = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await esignApi.getCosForm(id);
      setSchema(data.schema);
      const loaded = { ...(data.values || {}) };
      depositSet.current = !!String(loaded.deposit || '').trim();
      settlementDateSet.current = !!String(loaded.settlement_date || '').trim();
      // The sheet usually loads with a settlement period already set ("30 days"
      // from the linked offer) but a blank due date, and the period-driven
      // auto-fill only ran on change, so the agent had to clear and re-type the
      // period to populate the calendar. Fill the due date from that starting
      // period straight away, exactly as typing it would (DOMA 13213368559). A
      // date agreed by hand is left alone.
      if (!settlementDateSet.current && String(loaded.settlement_period || '').trim()) {
        loaded.settlement_date = settlementDateFromPeriod(loaded.settlement_period);
      }
      setValues(loaded);
      // Reveal purchaser 3/4 cards up front when a saved offer already has data in
      // them, so nothing entered earlier is hidden.
      const v = data.values || {};
      const filled = (prefix) =>
        Object.keys(v).some((k) => k.startsWith(prefix) && String(v[k] ?? '').trim());
      setVisiblePurchasers(filled('purchaser_4_') ? 4 : filled('purchaser_3_') ? 3 : 2);
      setMissing(data.missing || []);
      setHasContract(!!data.has_contract);
      setActiveKey((k) => k || data.schema?.sections?.[0]?.key || null);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not load the Contract of Sale.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // "10% Deposit (Auto-calculated)" is how the Buyer Portal asks the buyer, so
  // it is worked out the same way here - until someone sets a deposit of their
  // own, which includes one the buyer already named in their offer.
  const onChange = (key, val) => {
    if (key === 'deposit') depositSet.current = true;
    // A hand-edited settlement date wins; a cleared one re-opens auto-fill.
    if (key === 'settlement_date') settlementDateSet.current = !!String(val || '').trim();
    setValues((v) => {
      const next = { ...v, [key]: val };
      // Typing a settlement period ("30 days") fills the due date from today until
      // the agent sets a date by hand (DOMA 13213368559).
      if (key === 'settlement_period' && !settlementDateSet.current) {
        next.settlement_date = settlementDateFromPeriod(val);
      }
      if (key !== 'sale_price' && key !== 'deposit') return next;
      const price = money(next.sale_price);
      // Store the auto-calculated figures comma-free, exactly like the values the
      // agent types into the currency inputs (commaGroup re-adds the commas for
      // display), so everything in form_state is one consistent raw-number shape.
      if (key === 'sale_price' && price !== null && !depositSet.current) {
        next.deposit = rawMoney(price / 10);
      }
      const deposit = money(next.deposit);
      if (price !== null && deposit !== null && price >= deposit) {
        next.balance = rawMoney(price - deposit);
      } else {
        // Clear a now-stale balance when the price/deposit pair no longer makes
        // sense (deposit above price, or either cleared) - otherwise an earlier
        // auto-calc lingers and a wrong figure gets saved and baked onto the COS.
        next.balance = '';
      }
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await esignApi.saveCosForm(id, values);
      setValues(data.values || values);
      setMissing(data.missing || []);
      toast.success('Saved.');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not save.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const uploadContract = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await esignApi.cosSetContract(id, file);
      setHasContract(true);
      toast.success('Contract uploaded.');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not upload the contract.');
    } finally {
      setUploading(false);
    }
  };

  // Save the buyer details, then open the editor with ?autofill=1 so the fill
  // runs on arrival and the contract opens with these details already on it.
  const continueToContract = async () => {
    // Only proceed to the editor (which immediately autofills from the saved
    // server-side form_state) if the save actually succeeded - otherwise the
    // contract fills from stale/blank data and the agent's entries are lost.
    if (await save()) navigate(`/esign/envelope/${id}?autofill=1`);
  };

  if (loading || !schema) {
    return <div className="p-8 text-gray-500">Loading…</div>;
  }

  const section = schema.sections.find((s) => s.key === activeKey) || schema.sections[0];
  const missingSet = new Set(missing);
  // The next tab in order, so the agent can page through the sections from the
  // bottom of the form instead of hunting for the tab strip up top (DOMA
  // 13213246366). Undefined on the final section, where "Continue to contract
  // editor" is the forward action instead.
  const sectionIdx = schema.sections.findIndex((s) => s.key === section.key);
  const nextSection = schema.sections[sectionIdx + 1];

  // Purchaser cards (DOMA 13213227584). The schema tags purchaser questions with a
  // group + pindex; the highest pindex is the cap (4). Adding/removing is shared
  // across the Purchaser and Purchaser-contact tabs via visiblePurchasers.
  const allQuestions = schema.sections.flatMap((s) => s.questions);
  const maxPurchasers = Math.max(
    2, ...allQuestions.filter((q) => q.group === 'purchaser').map((q) => q.pindex || 0));
  const addPurchaser = () => setVisiblePurchasers((n) => Math.min(n + 1, maxPurchasers));
  const removePurchaser = (idx) => {
    const keys = allQuestions
      .filter((q) => q.group === 'purchaser' && q.pindex === idx)
      .map((q) => q.key);
    setValues((v) => {
      const next = { ...v };
      keys.forEach((k) => { next[k] = ''; });
      return next;
    });
    setVisiblePurchasers(Math.max(2, idx - 1));
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <button
        onClick={() => navigate('/esign/agreements')}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft size={16} /> Back to documents
      </button>

      <div className="flex items-center gap-2 mb-1 text-teal-600">
        <ScrollText size={18} />
        <span className="text-xs font-medium uppercase tracking-wide">Contract of Sale</span>
      </div>
      <h1 className="text-xl font-semibold mb-4">{schema.name}</h1>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <label className="inline-flex items-center gap-2 rounded-lg border border-teal-300 bg-white px-3 py-1.5 text-sm font-medium text-teal-700 cursor-pointer hover:bg-teal-50">
          <Upload size={15} />
          {uploading ? 'Uploading…' : hasContract ? 'Replace contract PDF' : 'Upload contract PDF'}
          <input
            type="file"
            accept="application/pdf"
            className="hidden"
            disabled={uploading}
            onChange={(e) => uploadContract(e.target.files?.[0])}
          />
        </label>
        <span className="text-xs text-gray-500">
          {hasContract ? '✓ Contract attached' : 'No contract uploaded yet'}
        </span>
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-5">
        {schema.sections.map((s) => {
          const nMiss = s.questions.filter((q) => missingSet.has(q.key)).length;
          const active = s.key === section.key;
          return (
            <button
              key={s.key}
              onClick={() => setActiveKey(s.key)}
              className={`px-3 py-2 text-sm -mb-px border-b-2 ${
                active
                  ? 'border-teal-500 text-teal-600 font-medium'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {s.title}
              {nMiss > 0 && <span className="ml-1 text-xs text-red-500">({nMiss})</span>}
            </button>
          );
        })}
      </div>

      {/* Fields for the active section */}
      {section.questions.some((q) => q.group === 'purchaser') ? (
        // Grouped: one card per purchaser, with a shared add/remove control.
        <div className="space-y-4">
          {Array.from({ length: visiblePurchasers }, (_, i) => i + 1)
            .map((idx) => [idx, section.questions.filter((q) => q.pindex === idx)])
            .filter(([, qs]) => qs.length)
            .map(([idx, qs]) => (
              <div key={idx} className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Purchaser {idx}</h3>
                  {idx === visiblePurchasers && idx > 2 && (
                    <button
                      type="button"
                      onClick={() => removePurchaser(idx)}
                      className="text-xs text-gray-400 hover:text-red-500"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {qs.map((q) => (
                    <Field key={q.key} q={q} value={values[q.key]}
                      onChange={onChange} invalid={missingSet.has(q.key)} />
                  ))}
                </div>
              </div>
            ))}
          {visiblePurchasers < maxPurchasers && (
            <button
              type="button"
              onClick={addPurchaser}
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-teal-400 px-3 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50"
            >
              <Plus size={15} /> Add purchaser
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {section.key === 'legal' && (
            <LegalRepPicker
              onPick={(c) => {
                if (c.name) onChange('legal_rep_name', c.name);
                if (c.address) onChange('legal_rep_address', c.address);
                if (c.phone) onChange('legal_rep_mobile', c.phone);
                if (c.email) onChange('legal_rep_email', c.email);
              }}
            />
          )}
          {section.questions.filter((q) => isVisible(q, values)).map((q) => (
            <div key={q.key}
              className={(q.type === 'textarea' || q.type === 'checkboxes') ? 'sm:col-span-2' : ''}>
              <Field q={q} value={values[q.key]} onChange={onChange} invalid={missingSet.has(q.key)} />
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg border border-teal-600 px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-50 disabled:opacity-60"
        >
          <Save size={16} /> {saving ? 'Saving…' : 'Save'}
        </button>
        {nextSection && (
          <button
            onClick={() => setActiveKey(nextSection.key)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Next: {nextSection.title} <ArrowRight size={16} />
          </button>
        )}
        <button
          onClick={continueToContract}
          disabled={saving || uploading || !hasContract}
          title={hasContract ? '' : 'Upload the lawyer contract first'}
          className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-60"
        >
          Continue to contract editor <ArrowRight size={16} />
        </button>
        {missing.length > 0 && (
          <span className="text-xs text-gray-400">{missing.length} required field(s) still blank</span>
        )}
      </div>
    </div>
  );
}
